(() => {
  if (customElements.get('pm-story-carousel')) return;

  class PictoMiniStoryCarousel extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.slides = [...this.querySelectorAll('[data-story-slide]')];
      this.dots = [...this.querySelectorAll('[data-story-dot]')];
      this.toggle = this.querySelector('[data-story-toggle]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.requestId = 0;
      this.pendingIndex = null;
      this.userPaused = false;
      this.hovering = this.matches(':hover');
      this.focused = this.contains(document.activeElement);
      this.lifecycle = new AbortController();
      const options = { signal: this.lifecycle.signal };

      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || button.closest('pm-story-carousel') !== this || button.disabled) return;
        const base = this.pendingIndex ?? this.index;
        if (button.hasAttribute('data-story-prev')) this.goTo(base - 1, true);
        else if (button.hasAttribute('data-story-next')) this.goTo(base + 1, true);
        else if (button.hasAttribute('data-story-dot')) this.goTo(Number(button.dataset.storyDot), true);
        else if (button === this.toggle) {
          this.userPaused = !this.userPaused;
          this.sync();
        }
      }, options);
      this.addEventListener('mouseenter', () => { this.hovering = true; this.sync(); }, options);
      this.addEventListener('mouseleave', () => { this.hovering = false; this.sync(); }, options);
      this.addEventListener('focusin', () => { this.focused = true; this.sync(); }, options);
      this.addEventListener('focusout', event => {
        this.focused = this.contains(event.relatedTarget);
        this.sync();
      }, options);
      this.addEventListener('pointerdown', event => {
        this.touch = event.isPrimary && event.pointerType === 'touch'
          ? { id: event.pointerId, x: event.clientX, y: event.clientY } : null;
      }, options);
      this.addEventListener('pointerup', event => {
        const start = this.touch;
        this.touch = null;
        if (!start || !event.isPrimary || event.pointerId !== start.id) return;
        const dx = event.clientX - start.x, dy = event.clientY - start.y;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          this.goTo((this.pendingIndex ?? this.index) + (dx < 0 ? 1 : -1), true);
        }
      }, options);
      this.addEventListener('pointercancel', () => { this.touch = null; }, options);
      document.addEventListener('visibilitychange', () => this.sync(), options);
      this.motion.addEventListener('change', () => this.sync(), options);

      this.commit(0);
      this.sync();
      // The first hero keeps network priority. Only warm a later image after it
      // has actually loaded and decoded, and keep subsequent requests low priority.
      const firstImage = this.slides[0]?.querySelector('img');
      if (firstImage) this.readyImage(firstImage, this.lifecycle.signal)
        .then(() => this.queuePrewarm()).catch(() => {});
      this.toggleAttribute('data-story-ready', true);
    }

    canAutoPlay() {
      return this.connected && !!this.toggle && this.slides.length > 1 && !this.userPaused
        && !this.hovering && !this.focused && !document.hidden && !this.motion.matches;
    }

    sync() {
      clearTimeout(this.timer);
      if (this.pendingReason === 'auto' && !this.canAutoPlay()) this.cancelPending();
      const selectedPause = this.userPaused || this.motion.matches;
      if (this.toggle) {
        this.toggle.disabled = this.slides.length < 2 || this.motion.matches;
        this.toggle.setAttribute('aria-label', selectedPause ? this.dataset.play : this.dataset.pause);
        this.toggle.setAttribute('aria-pressed', String(!selectedPause));
        const play = this.toggle.querySelector('[data-story-play-icon]');
        const pause = this.toggle.querySelector('[data-story-pause-icon]');
        if (play) play.toggleAttribute('hidden', !selectedPause);
        if (pause) pause.toggleAttribute('hidden', selectedPause);
      }
      this.setAttribute('data-story-autoplay', this.canAutoPlay() ? 'playing' : 'paused');
      if (this.canAutoPlay() && this.pendingIndex === null) {
        this.timer = setTimeout(() => this.goTo(this.index + 1), 7000);
      }
    }

    cancelPending() {
      this.requestId += 1;
      this.pendingController?.abort();
      this.pendingController = null;
      this.pendingIndex = null;
      this.pendingReason = null;
    }

    async goTo(requestedIndex, manual = false) {
      if (!this.connected || !this.slides.length || !Number.isInteger(requestedIndex)) return false;
      if (manual) this.userPaused = true;
      const target = ((requestedIndex % this.slides.length) + this.slides.length) % this.slides.length;
      this.cancelPending();
      if (target === this.index) { this.sync(); return true; }
      const token = this.requestId;
      const controller = new AbortController();
      this.pendingController = controller;
      this.pendingIndex = target;
      this.pendingReason = manual ? 'manual' : 'auto';
      this.sync();
      try {
        await this.readyImage(this.slides[target].querySelector('img'), controller.signal);
        if (!this.connected || token !== this.requestId || (!manual && !this.canAutoPlay())) return false;
        this.commit(target);
        this.queuePrewarm();
        return true;
      } catch (error) {
        if (error.name !== 'AbortError' && this.connected && token === this.requestId) {
          this.userPaused = true;
          this.dispatchEvent(new CustomEvent('story-carousel:error', { detail: { index: target, reason: 'image-unavailable' } }));
        }
        return false;
      } finally {
        if (token === this.requestId) {
          this.pendingController = null;
          this.pendingIndex = null;
          this.pendingReason = null;
          this.sync();
        }
      }
    }

    commit(index) {
      this.index = index;
      this.slides.forEach((slide, position) => {
        const inactive = position !== index;
        slide.hidden = inactive;
        slide.setAttribute('aria-hidden', String(inactive));
        slide.inert = inactive;
        slide.toggleAttribute('inert', inactive);
      });
      this.dots.forEach(dot => dot.setAttribute('aria-pressed', String(Number(dot.dataset.storyDot) === index)));
    }

    readyImage(image, signal) {
      return new Promise((resolve, reject) => {
        if (!image) { reject(new Error('Missing story image')); return; }
        if (signal.aborted) { reject(new DOMException('Cancelled', 'AbortError')); return; }
        let settled = false, decoding = false;
        const finish = error => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          image.removeEventListener('load', loaded);
          image.removeEventListener('error', failed);
          signal.removeEventListener('abort', aborted);
          if (error) reject(error); else resolve();
        };
        const aborted = () => finish(new DOMException('Cancelled', 'AbortError'));
        const failed = () => finish(new Error('Story image unavailable'));
        const loaded = async () => {
          if (decoding || settled) return;
          if (!image.naturalWidth) { failed(); return; }
          decoding = true;
          try {
            if (typeof image.decode === 'function') await image.decode();
            if (signal.aborted) aborted();
            else if (image.naturalWidth) finish();
            else failed();
          } catch (error) { finish(error); }
        };
        const timeout = setTimeout(failed, 20000);
        image.addEventListener('load', loaded);
        image.addEventListener('error', failed);
        signal.addEventListener('abort', aborted, { once: true });
        image.loading = 'eager';
        if (image.complete) {
          if (image.naturalWidth) loaded(); else failed();
        }
      });
    }

    queuePrewarm() {
      clearTimeout(this.prewarmTimer);
      if (!this.connected || this.slides.length < 2) return;
      const from = this.index;
      this.prewarmTimer = setTimeout(() => {
        if (!this.connected || this.index !== from || document.hidden) return;
        const next = (this.index + 1) % this.slides.length;
        if (this.pendingIndex === next) return;
        const image = this.slides[next].querySelector('img');
        if (!image || (image.complete && image.naturalWidth)) return;
        this.prewarmController?.abort();
        this.prewarmController = new AbortController();
        image.fetchPriority = 'low';
        this.readyImage(image, this.prewarmController.signal).catch(() => {});
      }, 450);
    }

    disconnectedCallback() {
      this.connected = false;
      this.toggleAttribute('data-story-ready', false);
      clearTimeout(this.timer);
      clearTimeout(this.prewarmTimer);
      this.lifecycle?.abort();
      this.prewarmController?.abort();
      this.cancelPending();
      this.touch = null;
    }
  }

  customElements.define('pm-story-carousel', PictoMiniStoryCarousel);
})();
